'use client'

import { useState, Suspense, useEffect } from 'react'
import { useSignUp, useUser } from '@clerk/nextjs'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Mail, Lock, User, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, Home, Gift } from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'

// Google SVG logo component
const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
)

function SignUpForm() {
  const { isLoaded, signUp, setActive } = useSignUp()
  const { isSignedIn } = useUser()
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get('redirect_url') || '/dashboard'
  const refFromUrl = searchParams.get('ref') || ''
  const [emailAddress, setEmailAddress] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [pendingVerification, setPendingVerification] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [referralCode, setReferralCode] = useState(refFromUrl)
  const [referralMessage, setReferralMessage] = useState('')
  const [showReferralInput, setShowReferralInput] = useState(!!refFromUrl)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle')
  const [signStep, setSignStep] = useState(1)
  const [direction, setDirection] = useState(0) // 1 for next, -1 for back
  const router = useRouter()

  // If user is already signed in, redirect them away from sign-up page
  useEffect(() => {
    if (isSignedIn) {
      router.replace(redirectUrl)
    }
  }, [isSignedIn, redirectUrl, router])

  // Username validation rules: 3-20 chars, lowercase alphanumeric + underscores
  const validateUsername = (val: string) => {
    if (!val) return false
    const regex = /^[a-z0-9_]{3,20}$/
    return regex.test(val)
  }

  // Debounced username availability check
  useEffect(() => {
    if (!username) {
      setUsernameStatus('idle')
      return
    }

    if (!validateUsername(username)) {
      setUsernameStatus('invalid')
      return
    }

    const timer = setTimeout(async () => {
      setUsernameStatus('checking')
      try {
        const response = await fetch(`/api/auth/check-username?username=${username}`)
        const data = await response.json()
        if (data.available) {
          setUsernameStatus('available')
        } else {
          setUsernameStatus('taken')
        }
      } catch (err) {
        console.error('Error checking username:', err)
        setUsernameStatus('idle')
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [username])

  // Handle Google OAuth sign-up
  const handleGoogleSignUp = async () => {
    if (!isLoaded || googleLoading) return
    setGoogleLoading(true)
    setError('')
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: redirectUrl,
      })
    } catch (err: any) {
      setGoogleLoading(false)
      setError(err.errors?.[0]?.longMessage || 'Google sign-up failed. Please try again.')
    }
  }

  // Handle submission of the sign-up form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded) return

    setLoading(true)
    setError('')

    try {
      // Split full name into first and last name for Clerk
      const nameParts = fullName.trim().split(/\s+/)
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      await signUp.create({
        emailAddress,
        password,
        firstName,
        lastName,
      })

      // Send the verification email
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' })

      setPendingVerification(true)
    } catch (err: any) {
      setError(err.errors[0]?.longMessage || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle submission of the verification code
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded) return

    setLoading(true)
    setError('')

    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code,
      })

      if (completeSignUp.status === 'complete') {
        await setActive({ session: completeSignUp.createdSessionId })

        // Create Supabase profile after successful verification
        const response = await fetch('/api/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            display_name: fullName
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to create profile');
        }

        // Apply referral code if provided
        if (referralCode.trim()) {
          try {
            const refRes = await fetch('/api/user-referrals/apply', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: referralCode.trim() }),
            });
            if (refRes.ok) {
              setReferralMessage('Referral applied! You got 14 days free Pro.')
            }
          } catch (refErr) {
            console.error('Referral apply error:', refErr)
          }
        }

        router.push(redirectUrl)
      }
    } catch (err: any) {
      setError(err.message || err.errors?.[0]?.longMessage || 'Invalid verification code.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-svh w-full flex items-center justify-center p-6 bg-black overflow-hidden">
      <GeometricBackground />

      {/* Back to Home Button */}
      <div className="absolute top-6 left-6 z-20">
        <Link
          href="/"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/50 hover:text-white hover:bg-white/[0.06] transition-all text-sm font-medium backdrop-blur-sm group"
        >
          <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
          Back to Home
        </Link>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-8 shadow-2xl"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white tracking-tight mb-2">
              {pendingVerification ? 'Verify Email' : 
               signStep === 1 ? 'Join Lenklyst' :
               signStep === 2 ? 'Your Identity' :
               'Secure Account'}
            </h1>
            <p className="text-white/40 text-sm">
              {pendingVerification
                ? `Enter the code sent to ${emailAddress}`
                : signStep === 1 ? 'The marketing and branding ecosystem for creators, marketers, and founders' :
                   signStep === 2 ? 'Choose your network identity' :
                   'Set your secure access credentials'}
            </p>
          </div>

          {/* Progress Bar */}
          {!pendingVerification && (
            <div className="flex gap-2 mb-8 px-1">
              {[1, 2, 3].map((step) => (
                <div 
                  key={step} 
                  className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                    step <= signStep ? 'bg-primary' : 'bg-white/5'
                  }`}
                />
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            {!pendingVerification ? (
              <motion.form
                key={`step-${signStep}`}
                initial={{ opacity: 0, x: direction * 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -20 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                onSubmit={(e) => {
                  e.preventDefault()
                  if (signStep < 3) {
                    setDirection(1)
                    setSignStep(s => s + 1)
                  } else {
                    handleSubmit(e)
                  }
                }}
                className="space-y-5"
              >
                <div className="space-y-4">
                  {signStep === 1 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                          Full Name
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                            <User className="w-4 h-4" />
                          </div>
                          <input
                            type="text"
                            required
                            autoFocus
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-medium"
                          />
                        </div>
                      </div>
                      {/* Referral Code — optional */}
                      <div>
                        {!showReferralInput ? (
                          <button
                            type="button"
                            onClick={() => setShowReferralInput(true)}
                            className="text-xs text-primary/70 hover:text-primary transition-colors hover:underline focus:outline-none ml-1 flex items-center gap-1.5"
                          >
                            <Gift className="w-3.5 h-3.5" />
                            Have a referral code?
                          </button>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="space-y-1.5"
                          >
                            <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                              Referral Code <span className="text-white/20 normal-case">(optional)</span>
                            </label>
                            <div className="relative group">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                                <Gift className="w-4 h-4" />
                              </div>
                              <input
                                type="text"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                                placeholder="e.g. LL-A3X7K9"
                                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-mono tracking-wider"
                                maxLength={12}
                              />
                            </div>
                            <p className="text-[10px] text-white/15 mt-1.5 ml-1">Get 14 days free Pro when you apply a friend&apos;s code!</p>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {signStep === 2 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                          Email Address
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                            <Mail className="w-4 h-4" />
                          </div>
                          <input
                            type="email"
                            required
                            autoFocus
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-medium"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                          Username
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                            <User className="w-4 h-4" />
                          </div>
                          <input
                            type="text"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                            className={`w-full bg-white/[0.03] border rounded-xl py-3 pl-11 pr-10 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 transition-all font-medium ${
                              usernameStatus === 'available' ? 'border-emerald-500/50 focus:ring-emerald-500/10' :
                              usernameStatus === 'taken' || usernameStatus === 'invalid' ? 'border-red-500/50 focus:ring-red-500/10' :
                              'border-white/[0.08] focus:ring-primary/20 focus:border-primary/40'
                            }`}
                          />
                          <div className="absolute inset-y-0 right-3 flex items-center">
                            {usernameStatus === 'checking' && <Loader2 className="w-4 h-4 text-white/20 animate-spin" />}
                            {usernameStatus === 'available' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                            {(usernameStatus === 'taken' || usernameStatus === 'invalid') && <AlertCircle className="w-4 h-4 text-red-500" />}
                          </div>
                        </div>
                        {usernameStatus !== 'idle' && (
                          <div className="mt-1.5 ml-1">
                            {usernameStatus === 'invalid' && (
                              <p className="text-[10px] text-red-400/80 font-medium">3-20 characters, lowercase, numbers or underscores only</p>
                            )}
                            {usernameStatus === 'taken' && (
                              <p className="text-[10px] text-red-400/80 font-medium">This username is already taken</p>
                            )}
                            {usernameStatus === 'available' && (
                              <p className="text-[10px] text-emerald-400/80 font-medium">Username is available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {signStep === 3 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                          Password
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                            <Lock className="w-4 h-4" />
                          </div>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            required
                            autoFocus
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-12 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/20 hover:text-white/40 transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {password && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-3 space-y-3 overflow-hidden"
                          >
                            {/* Strength Bar */}
                            <div className="flex gap-1 h-1">
                              {[1, 2, 3, 4, 5].map((level) => {
                                const strength = [
                                  password.length >= 8,
                                  /[A-Z]/.test(password),
                                  /[a-z]/.test(password),
                                  /[0-9]/.test(password),
                                  /[^A-Za-z0-9]/.test(password),
                                ].filter(Boolean).length

                                let color = 'bg-white/5'
                                if (level <= strength) {
                                  if (strength <= 2) color = 'bg-red-500/50'
                                  else if (strength <= 4) color = 'bg-orange-500/50'
                                  else color = 'bg-emerald-500/50'
                                }

                                return (
                                  <div
                                    key={level}
                                    className={`flex-1 rounded-full transition-colors duration-500 ${color}`}
                                  />
                                )
                              })}
                            </div>

                            {/* Requirements Checklist */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-1 text-[10px]">
                              {[
                                { label: '8+ Characters', met: password.length >= 8 },
                                { label: 'Uppercase', met: /[A-Z]/.test(password) },
                                { label: 'Lowercase', met: /[a-z]/.test(password) },
                                { label: 'Number', met: /[0-9]/.test(password) },
                                { label: 'Special Char', met: /[^A-Za-z0-9]/.test(password) },
                              ].map((req) => (
                                <div key={req.label} className="flex items-center gap-2">
                                  {req.met ? (
                                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                  ) : (
                                    <div className="w-3 h-3 rounded-full border border-white/10" />
                                  )}
                                  <span className={`transition-colors ${req.met ? 'text-white/60' : 'text-white/20'}`}>
                                    {req.label}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                          Confirm Password
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                            <Lock className="w-4 h-4" />
                          </div>
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={`w-full bg-white/[0.03] border rounded-xl py-3 pl-11 pr-12 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 transition-all font-mono ${
                              confirmPassword && password !== confirmPassword ? 'border-red-500/50 focus:ring-red-500/10' :
                              confirmPassword && password === confirmPassword ? 'border-emerald-500/50 focus:ring-emerald-500/10' :
                              'border-white/[0.08] focus:ring-primary/20 focus:border-primary/40'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/20 hover:text-white/40 transition-colors"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {confirmPassword && (
                          <div className="mt-1.5 ml-1 text-[10px] font-medium transition-all">
                            {password !== confirmPassword ? (
                              <p className="text-red-400/80">Passwords do not match</p>
                            ) : (
                              <p className="text-emerald-400/80">Passwords match</p>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <div className="flex gap-3 pt-2">
                  {signStep > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setDirection(-1)
                        setSignStep(s => s - 1)
                      }}
                      className="flex-1 h-12 bg-white/[0.03] hover:bg-white/[0.06] text-white/50 hover:text-white rounded-xl font-semibold transition-all flex items-center justify-center border border-white/5"
                    >
                      Back
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={
                      loading || googleLoading ||
                      (signStep === 1 && !fullName.trim()) ||
                      (signStep === 2 && (!emailAddress.trim() || usernameStatus !== 'available')) ||
                      (signStep === 3 && (
                        password !== confirmPassword ||
                        !(
                          password.length >= 8 &&
                          /[A-Z]/.test(password) &&
                          /[a-z]/.test(password) &&
                          /[0-9]/.test(password) &&
                          /[^A-Za-z0-9]/.test(password)
                        )
                      ))
                    }
                    className="flex-[2] h-12 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-primary/20"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {signStep === 3 ? 'Create Account' : 'Continue'}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>

                {/* Terms link moved to bottom of card */}

                {signStep === 1 && (
                  <>
                    <div className="relative flex items-center gap-4 my-1">
                      <div className="flex-1 h-px bg-white/[0.08]" />
                      <span className="text-[11px] text-white/25 uppercase tracking-widest font-medium">or</span>
                      <div className="flex-1 h-px bg-white/[0.08]" />
                    </div>

                    {/* Google Sign Up Button */}
                    <button
                      type="button"
                      onClick={handleGoogleSignUp}
                      disabled={googleLoading}
                      className={`w-full h-12 border text-white rounded-xl font-medium flex items-center justify-center gap-3 transition-all active:scale-[0.98] group ${
                        googleLoading
                          ? 'bg-white/[0.06] border-primary/30 cursor-not-allowed'
                          : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/[0.08] hover:border-white/[0.15]'
                      }`}
                    >
                      {googleLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                          <span className="text-sm text-white/70">Redirecting to Google...</span>
                        </>
                      ) : (
                        <>
                          <GoogleLogo />
                          <span className="text-sm text-white/70 group-hover:text-white transition-colors">Continue with Google</span>
                        </>
                      )}
                    </button>
                  </>
                )}

                <div className="text-center mt-6">
                  <p className="text-white/30 text-xs">
                    Already have an account?{' '}
                    <Link href="/auth/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                      Sign In
                    </Link>
                  </p>
                </div>
              </motion.form>
            ) : (
              <motion.form
                key="verification-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleVerify}
                className="space-y-6"
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                      Verification Code
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all text-center tracking-[0.5em] text-xl font-bold"
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-primary/20"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Complete Registration'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingVerification(false)}
                    className="w-full h-12 bg-white/[0.03] hover:bg-white/[0.06] text-white/60 hover:text-white rounded-xl font-medium transition-all text-sm"
                  >
                    Back to Edit
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {!pendingVerification && (
            <p className="text-[10px] text-white/20 text-center px-4 leading-relaxed mt-6 border-t border-white/[0.04] pt-4">
              By signing up, you agree to our{' '}
              <Link href="/legal/terms" className="text-white/40 hover:text-primary transition-colors underline underline-offset-2">Terms of Service</Link>
              {' '}and{' '}
              <Link href="/legal/privacy" className="text-white/40 hover:text-primary transition-colors underline underline-offset-2">Privacy Policy</Link>.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default function SignUpPage() {
  return (
    <Suspense fallback={
      <div className="min-h-svh w-full flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <SignUpForm />
    </Suspense>
  )
}
